<template>
  <v-container class="automatic-stake-form">
    <!-- Header with action buttons -->
    <div class="action-buttons">
      <v-btn color="accent" @click="createNewWithGPT" :disabled="isGptLoading">
        <v-icon v-if="isGptLoading" left class="mdi-spin">mdi-loading</v-icon>
        <v-icon v-else left>mdi-lightning-bolt</v-icon>
        {{ isGptLoading ? "Generating..." : "AI Generate" }}
      </v-btn>
      <v-btn color="primary" @click="openSelectionModal" class="mr-2">
        <v-icon left>mdi-plus-circle</v-icon>
        Add Stakeholders
      </v-btn>
    </div>

    <!-- Selected Stakeholders List -->
    <div v-if="selectedStakeholderList.length === 0" class="empty-state">
      <v-icon size="80" color="primary">mdi-account-multiple-outline</v-icon>
      <h3>No Stakeholders added to the problem</h3>
      <p>Add or select stakeholders</p>
    </div>

    <div v-else class="stakeholders-container">
      <div class="list">
        <stakeholder-card
          v-for="stakeholder in selectedStakeholderList"
          :key="stakeholder.id"
          :stakeholder="stakeholder"
          @delete="removeFromSelectedList"
          @edit="openEditModal"
        />
      </div>
    </div>

    <!-- Stakeholder Selection Modal -->
    <stakeholder-selection-modal
      v-model="showSelectionModal"
      :stakeholders="allStakeholders"
      :selected-stakeholder-ids="selectedStakeholderIds"
      @select="addStakeholderToList"
      @create-new="createNewManually"
      @toggle="toggleStakeholderSelection"
      @close="showSelectionModal = false"
    />

    <!-- Stakeholder Edit Modal -->
    <stakeholder-edit-modal
      :value="showEditModal"
      :stakeholder="stakeholderToEdit"
      :saving="isSaving"
      @close="showEditModal = $event"
      @save="saveStakeholder"
      @delete="confirmDeleteStakeholder"
    />

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="confirmDialog" max-width="450">
      <v-card class="confirm-dialog">
        <v-card-title class="confirm-dialog__title">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Confirm Deletion
        </v-card-title>
        <v-card-text class="confirm-dialog__content">
          <p>Are you sure you want to delete this stakeholder?</p>
          <p v-if="stakeholderToDelete" class="stakeholder-name">
            {{ stakeholderToDelete.name }}
          </p>
          <p class="warning-text">This action cannot be undone.</p>
        </v-card-text>
        <v-card-actions class="confirm-dialog__actions">
          <v-spacer></v-spacer>
          <v-btn text @click="confirmDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteStakeholder">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :timeout="3000"
      :color="snackbarColor"
      @click="snackbar = false"
      style="cursor: pointer"
    >
      <v-icon left v-if="snackbarColor === 'success'">mdi-check-circle</v-icon>
      <v-icon left v-else-if="snackbarColor === 'error'"
        >mdi-alert-circle</v-icon
      >
      <v-icon left v-else>mdi-information</v-icon>
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import StakeholderCard from "./StakeholderCard.vue";
import StakeholderSelectionModal from "./StakeholderSelectionModal.vue";
import StakeholderEditModal from "./StakeholderEditModal.vue";
import apiClient from "@/plugins/apiService";

export default {
  name: "AutomaticStakeForm",

  components: {
    StakeholderCard,
    StakeholderSelectionModal,
    StakeholderEditModal,
  },

  props: {
    selectedLab: {
      type: [String, Number],
      default: null,
    },
    selectedProblem: {
      type: [String, Number],
      default: null,
    },
  },

  data() {
    return {
      // Stakeholders data
      allStakeholders: [],
      selectedStakeholderList: [],
      selectedStakeholderId: null,

      // Modal states
      showSelectionModal: false,
      showEditModal: false,
      stakeholderToEdit: null,

      // Delete confirmation
      confirmDialog: false,
      stakeholderToDelete: null,

      // Loading states
      isSaving: false,
      isGptLoading: false,

      // Snackbar
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
    };
  },

  computed: {
    selectedStakeholder() {
      return this.selectedStakeholderList.find(
        (s) => s.id === this.selectedStakeholderId
      );
    },

    selectedStakeholderIds() {
      return this.selectedStakeholderList.map((s) => s.id);
    },
  },

  watch: {
    selectedProblem() {
      this.fetchStakeholders();
    },
    selectedLab() {
      this.fetchStakeholders();
    },
  },

  mounted() {
    this.fetchStakeholders();
  },

  methods: {
    // ==================== Modal Controls ====================
    openSelectionModal() {
      this.showSelectionModal = true;
    },

    openEditModal(stakeholder) {
      this.stakeholderToEdit = { ...stakeholder };
      this.showEditModal = true;
    },

    // ==================== Selection Methods ====================
    selectStakeholder(stakeholder) {
      this.selectedStakeholderId = stakeholder.id;
    },

    async addStakeholderToList(stakeholder) {
      if (stakeholder?.error) {
        this.showErrorMessage(stakeholder.error);
        throw new Error(stakeholder.error);
      }

      if (this.selectedStakeholderIds.includes(stakeholder.id)) {
        return;
      }

      this.selectedStakeholderList.push(stakeholder);

      try {
        const response = await apiClient.put(
          `/add-stakeholder/${stakeholder.id}`,
          { selectedProblem: this.selectedProblem }
        );

        if (response.status !== 200) {
          throw new Error("Failed to add stakeholder");
        }

        this.showSuccessMessage("Stakeholder added to selection");
      } catch (error) {
        console.error("Error adding stakeholder to list:", error);
        // Remove from local list if API call failed
        const index = this.selectedStakeholderList.findIndex(
          (s) => s.id === stakeholder.id
        );
        if (index !== -1) {
          this.selectedStakeholderList.splice(index, 1);
        }
        this.showErrorMessage("Failed to add stakeholder");
        throw error;
      }
    },

    async removeFromSelectedList(stakeholderId) {
      const index = this.selectedStakeholderList.findIndex(
        (item) => item.id === stakeholderId
      );

      if (index === -1) return;

      const removedStakeholder = this.selectedStakeholderList[index];
      this.selectedStakeholderList.splice(index, 1);

      try {
        const response = await apiClient.delete(
          `/delete-stakeholder/${stakeholderId}`
        );

        if (response.status !== 200) {
          throw new Error("Failed to remove stakeholder");
        }

        // Clear selection if needed
        if (this.selectedStakeholderId === stakeholderId) {
          this.selectedStakeholderId = null;
        }

        this.showSuccessMessage("Stakeholder removed");
      } catch (error) {
        console.error("Error removing stakeholder:", error);
        // Restore the stakeholder to the list if API call failed
        this.selectedStakeholderList.splice(index, 0, removedStakeholder);
        this.showErrorMessage("Failed to remove stakeholder");
      }
    },

    async toggleStakeholderSelection(stakeholder) {
      if (this.selectedStakeholderIds.includes(stakeholder.id)) {
        await this.removeFromSelectedList(stakeholder.id);
      } else {
        await this.addStakeholderToList(stakeholder);
      }
    },

    // ==================== Creation Methods ====================
    createNewManually() {
      const newTempStakeholder = {
        id: "temp-" + new Date().getTime(),
        name: "New Stakeholder",
        general_description: "",
        requirements: "",
        specificities: "Reviewed Stakeholder",
        related_kpi: "",
        temp: true,
      };

      this.stakeholderToEdit = newTempStakeholder;
      this.showSelectionModal = false;
      this.showEditModal = true;
    },

    async createNewWithGPT() {
      this.isGptLoading = true;

      try {
        const response = await apiClient.post(`/gpt-stakeholder`, {
          selectedProblem: this.selectedProblem,
          selectedLab: this.selectedLab,
        });

        if (response.status === 201) {
          const newGPTStakeholder = response.data;
          this.selectedStakeholderList.push(newGPTStakeholder);
          this.selectedStakeholderId = newGPTStakeholder.id;
          this.showSuccessMessage(
            "AI-generated stakeholder created successfully"
          );
          await this.fetchStakeholders();
        } else {
          throw new Error("Failed to generate stakeholder with AI");
        }
      } catch (error) {
        console.error("Error creating GPT stakeholder:", error);
        this.showErrorMessage("Failed to generate stakeholder with AI");
      } finally {
        this.isGptLoading = false;
      }
    },

    // ==================== API Methods ====================
    async fetchStakeholders() {
      if (!this.selectedProblem) return;

      try {
        const response = await apiClient.get(
          `/stakeholder/${this.selectedProblem}`
        );
        const data = response.data;

        // All available stakeholders
        this.allStakeholders = data;

        // Currently selected stakeholders
        this.selectedStakeholderList = data.filter(
          (s) => s.selectedStakeholder
        );

        if (
          this.selectedStakeholderList.length > 0 &&
          !this.selectedStakeholderId
        ) {
          this.selectedStakeholderId = this.selectedStakeholderList[0].id;
        }
      } catch (error) {
        console.error("Error fetching stakeholders:", error);
        this.allStakeholders = [];
        this.selectedStakeholderList = [];
        this.showErrorMessage("Failed to load stakeholders");
      }
    },

    async saveStakeholder(stakeholderData) {
      this.isSaving = true;

      try {
        const isUpdate =
          stakeholderData.id &&
          !stakeholderData.id.toString().startsWith("temp-");
        const url = isUpdate
          ? `/stakeholder/${stakeholderData.id}`
          : `/stakeholder`;

        const payload = {
          ...stakeholderData,
          selectedProblem: this.selectedProblem,
          selectedLab: this.selectedLab,
        };

        const response = isUpdate
          ? await apiClient.put(url, payload)
          : await apiClient.post(url, payload);

        if (response.status === 200 || response.status === 201) {
          if ("error" in response.data) throw new Error(response.data.error);

          const savedStakeholder = response.data;

          if (!isUpdate) {
            // For new stakeholders, update the temp ID
            stakeholderData.id = savedStakeholder.id;
            delete stakeholderData.temp;
          }

          // Update or add to the lists
          const index = this.selectedStakeholderList.findIndex(
            (s) =>
              s.id === (isUpdate ? stakeholderData.id : savedStakeholder.id)
          );

          if (index !== -1) {
            this.selectedStakeholderList.splice(index, 1, savedStakeholder);
          } else {
            this.selectedStakeholderList.push(savedStakeholder);
          }

          this.selectedStakeholderId = savedStakeholder.id;
          this.showSelectionModal = false;
          this.showEditModal = false;
          this.showSuccessMessage(
            isUpdate ? "Stakeholder updated" : "Stakeholder created"
          );

          await this.fetchStakeholders();
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.error("Error saving stakeholder:", error);
        this.showErrorMessage("Failed to save stakeholder");
      } finally {
        this.isSaving = false;
      }
    },

    // ==================== Delete Operations ====================
    confirmDeleteStakeholder(stakeholder) {
      this.stakeholderToDelete = stakeholder;
      this.confirmDialog = true;
      this.showEditModal = false;
    },

    async deleteStakeholder() {
      if (!this.stakeholderToDelete) return;

      try {
        const response = await apiClient.delete(
          `/stakeholder/${this.stakeholderToDelete.id}`
        );

        if (response.status === 200) {
          const index = this.selectedStakeholderList.findIndex(
            (s) => s.id === this.stakeholderToDelete.id
          );
          if (index !== -1) {
            this.selectedStakeholderList.splice(index, 1);
          }

          if (this.selectedStakeholderId === this.stakeholderToDelete.id) {
            this.selectedStakeholderId = null;
          }

          this.confirmDialog = false;
          this.showSuccessMessage("Stakeholder deleted successfully");
          await this.fetchStakeholders();
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
      } catch (error) {
        console.error("Error deleting stakeholder:", error);
        this.showErrorMessage("Failed to delete stakeholder");
      } finally {
        this.stakeholderToDelete = null;
      }
    },

    // ==================== Notifications ====================
    showSuccessMessage(message) {
      this.snackbarMessage = message;
      this.snackbarColor = "success";
      this.snackbar = true;
    },

    showErrorMessage(message) {
      this.snackbarMessage = message;
      this.snackbarColor = "error";
      this.snackbar = true;
    },
  },
};
</script>

<style scoped>
.action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 18px;
  text-align: center;
}

/* Stakeholder List */
.stakeholders-container {
  display: flex;
  flex-direction: column;
  max-height: 700px;
  overflow-y: auto;
}
</style>
